import type { TreeFolder } from "@/domains/documents/types";
import {
  flattenDocumentRows,
  selectableItemsFromRows,
  type FlattenedRow,
} from "@/domains/documents/utils/flattenDocumentRows";
import { type FolderPathSegment } from "@/domains/documents/utils/folderPathResolver";
import { useMemo, useState } from "react";
import { type UnifiedItem } from "./documentTableSelection";
import type { DocumentWithTags } from "./documentTableTypes";

import type { documentTableOptions } from "./documentTableOptions";
import type { useTableDialogState } from "./useTableDialogState";
import type { useTableDocumentKinds } from "./useTableDocumentKinds";
export type FlattenedItem = FlattenedRow<DocumentWithTags, TreeFolder, FolderPathSegment>;
type Options = Pick<
  ReturnType<typeof documentTableOptions>,
  "versionedFolder" | "orderedSearchRows" | "folders"
> &
  Pick<ReturnType<typeof useTableDocumentKinds>, "displayDocuments" | "containerFiles"> &
  Pick<ReturnType<typeof useTableDialogState>, "versionExpansion" | "versionDocsCache">;
export function useTableRows(options: Options) {
  const {
    versionedFolder,
    orderedSearchRows,
    displayDocuments,
    folders,
    containerFiles,
    versionExpansion,
    versionDocsCache,
  } = options;
  /**
   * Every row the table paints, in render order: folder rows, container rows,
   * then documents — each anchor document followed by its expanded older
   * versions and a trailing "View full history" link. Each entry carries an
   * `anchorId` so keys stay stable across expand/collapse and sorting. In
   * search mode (orderedSearchRows) folders interleave with document hits
   * instead, and document hits expand their versions there exactly as they do
   * when browsing. Ordering logic lives in `flattenDocumentRows` so it can be
   * tested on its own.
   *
   * Folders and containers belong in THIS list, not in a block above the
   * virtualizer: they share the virtualizer's scroll container, and content it
   * does not know about shifts every row it positions (see the module comment
   * on `flattenDocumentRows`).
   */

  // The versioned folder's list, opened from its row's badge. Local: its
  // versions come with the prop, so there is nothing to fetch.
  const [folderVersionsOpen, setFolderVersionsOpen] = useState(false);
  const expandedFolderVersions = useMemo(
    () =>
      folderVersionsOpen && versionedFolder && versionedFolder.versions.length > 0
        ? { folderId: versionedFolder.folderId, versions: versionedFolder.versions }
        : null,
    [folderVersionsOpen, versionedFolder]
  );

  const flattenedItems = useMemo<FlattenedItem[]>(
    () =>
      flattenDocumentRows({
        orderedRows: orderedSearchRows,
        documents: displayDocuments,
        // Browse mode only — `flattenDocumentRows` ignores both when
        // `orderedRows` is supplied, matching the old `!orderedSearchRows`
        // guards on the blocks these replace.
        folders,
        containerDocs: containerFiles,
        expandedVersionGroups: versionExpansion.expandedGroups,
        versionDocsCache,
        expandedFolderVersions,
      }),
    [
      orderedSearchRows,
      displayDocuments,
      folders,
      containerFiles,
      versionExpansion.expandedGroups,
      versionDocsCache,
      expandedFolderVersions,
    ]
  );

  // Unified item list for shift-click range selection across folders, container
  // files and documents. Derived from the rows the virtualizer actually paints,
  // so selection order can never drift from render order — the drift that left
  // container files out of every range while they rendered in a block of their
  // own.
  const unifiedItems = useMemo<UnifiedItem[]>(
    () => selectableItemsFromRows(flattenedItems),
    [flattenedItems]
  );

  // Virtualizer position ≠ position in `unifiedItems`: expanded version rows and
  // the "view full history" link take virtualizer slots without being selectable
  // items, and folders sit inside the virtualizer in search mode but above it
  // when browsing. Range selection therefore resolves its index by id, never by
  // arithmetic on the rendered row's index.
  const unifiedIndexById = useMemo(() => {
    const map = new Map<string, number>();
    unifiedItems.forEach((item, i) => map.set(`${item.type}:${item.id}`, i));
    return map;
  }, [unifiedItems]);
  return {
    flattenedItems,
    unifiedItems,
    unifiedIndexById,
    folderVersionsOpen,
    setFolderVersionsOpen,
  };
}
